import React from 'react';
import { gql } from '@soundxyz/gql-string';
import type { ComponentProps } from '@components-shared/src/stitches';
import { styled } from '@components-shared/src/stitches';
import { CopyAddress } from '@components/CopyAddress';
import { Avatar } from '@components/basics/Images/Avatar';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { MediaQuery } from '@context/mediaQuery';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { UserDashboardInfoFragmentDoc } from '@graphql/reactQuery';
import { useHeaderStore } from '@store/header';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment UserDashboardInfo on User {
    id
    avatar {
      id
      url
    }
    publicAddress
    webappUri
    username
  }
`);

const AVATAR_SIZE_DESKTOP = 32;
const AVATAR_SIZE_MOBILE = 48;

export const UserInfo = ({
  userInfo,
  ...props
}: {
  userInfo: FragmentType<UserDashboardInfoFragmentDoc>;
} & ComponentProps<typeof Container>) => {
  const { close } = useHeaderStore();
  const { tablet } = MediaQuery.useContainer();
  const user = getFragment(UserDashboardInfoFragmentDoc, userInfo);
  const { publicAddress, avatar } = user;
  const name = user.username;

  return (
    <Container {...props}>
      <ImageContainer
        onClick={close}
        mobileNavOptions={props.mobileNavOptions || props.newMobileNav}
      >
        <Avatar
          size={tablet ? AVATAR_SIZE_DESKTOP : AVATAR_SIZE_MOBILE}
          src={avatar?.url}
          walletAddress={publicAddress}
          borderRadius={1000}
          disableLink={!props.desktopNavHeader}
          username={name}
          webappUri={user.webappUri}
        />
      </ImageContainer>
      <UserInfoContainer mobileNavOptions={props.mobileNavOptions || props.newMobileNav}>
        <h1>
          {props.desktopNavHeader ? (
            <AccessibleLink onClick={close} pathnameUri={webappUriPath(user.webappUri)}>
              {name}
            </AccessibleLink>
          ) : (
            name
          )}
        </h1>
        <SubUserInfo>
          <CopyAddress publicAddress={publicAddress} />
        </SubUserInfo>
      </UserInfoContainer>
    </Container>
  );
};

const Container = styled('div', {
  display: 'flex',
  gap: 12,
  marginTop: 20,
  variants: {
    mobileNavOptions: {
      true: {
        height: '100%',
        marginTop: 0,
        alignItems: 'center',
      },
    },
    desktopNavHeader: {
      true: {
        alignItems: 'center',
        margin: 16,
      },
    },

    newMobileNav: {
      true: {
        margin: 0,
      },
    },
  },
});

const ImageContainer = styled('div', {
  display: 'flex',
  flexShrink: 0,
  width: AVATAR_SIZE_DESKTOP,
  height: AVATAR_SIZE_DESKTOP,
  variants: {
    mobileNavOptions: {
      true: {
        width: AVATAR_SIZE_MOBILE,
        height: AVATAR_SIZE_MOBILE,
      },
    },
    newMobileNav: {
      true: {
        width: AVATAR_SIZE_MOBILE,
        height: AVATAR_SIZE_MOBILE,
      },
    },
  },
});

const UserInfoContainer = styled('div', {
  display: 'flex',
  flexGrow: 1,
  flexDirection: 'column',
  justifyContent: 'center',
  overflow: 'hidden',
  gap: 8,

  '@tablet': {
    gap: 0,
  },

  h1: {
    base: 'L',
    fontWeight: 600,
    color: '$neutral900',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },

  '.publicAddress': {
    base: 'S',
    color: '$base500',
    fontWeight: 500,
  },
  variants: {
    mobileNavOptions: {
      true: {
        height: '100%',
        justifyContent: 'space-around',
        flexGrow: 0,
        h1: {
          fontSize: 18,
          fontWeight: 600,
          color: '$black',
        },
        '.publicAddress': {
          base: 'M',
          color: '$base500',
          fontWeight: 500,
        },
      },
    },
  },
});

const SubUserInfo = styled('div', {
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  position: 'relative',
  gap: 6,
  '.copy-address-button': {
    width: 16,
    height: 10,
  },
  '@tablet': {
    '.copy-address-button': {
      marginTop: 0,
      height: 14,
    },
  },
});
